'use client';

/**
 * ProtectedRoute Component
 *
 * Route guard component that implements role-based access control (RBAC)
 * for protected routes in the RWA Terminal application.
 *
 * This component:
 * - Checks if the user is authenticated
 * - Verifies if the user's role is in the allowed roles list (if specified)
 * - Shows a loading spinner while auth state is being initialized
 * - Redirects to /login if user is not authenticated (preserving return URL)
 * - Redirects to /unauthorized if user's role is not allowed
 *
 * Usage:
 * ```tsx
 * <Route
 *   path="/admin"
 *   element={
 *     <ProtectedRoute allowedRoles={['publisher']}>
 *       <AdminPage />
 *     </ProtectedRoute>
 *   }
 * />
 * ```
 */

import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import type { Role } from '@/types';

/**
 * Props for the ProtectedRoute component
 */
interface ProtectedRouteProps {
  /** Child components to render when access is granted */
  children: ReactNode;
  /**
   * Optional array of roles that are allowed to access this route.
   * If not specified, any authenticated user can access the route.
   */
  allowedRoles?: Role[];
  /**
   * Custom redirect path for unauthorized access.
   * Defaults to '/unauthorized'.
   */
  unauthorizedRedirect?: string;
  /**
   * Custom redirect path for unauthenticated users.
   * Defaults to '/login'.
   */
  loginRedirect?: string;
}

/**
 * Loading spinner component displayed while authentication state is being loaded
 */
function LoadingSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 dark:bg-slate-900">
      <div className="flex flex-col items-center gap-4">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600 dark:border-primary-800 dark:border-t-primary-400" />
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Verifying access...
        </p>
      </div>
    </div>
  );
}

/**
 * ProtectedRoute - Authentication and authorization guard component
 *
 * This component implements a multi-layer access control:
 *
 * 1. **Loading State**: While auth state is being initialized (token validation),
 *    shows a loading spinner to prevent flash of unauthorized content.
 *
 * 2. **Authentication Check**: If no user is logged in, redirects to the login
 *    page with the current location preserved in state for post-login redirect.
 *
 * 3. **Authorization Check**: If allowedRoles is specified, verifies the user's
 *    role is in the list. Redirects to unauthorized page if not allowed.
 *
 * @example
 * // Allow only publishers to access admin routes
 * <ProtectedRoute allowedRoles={['publisher']}>
 *   <AdminUsersPage />
 * </ProtectedRoute>
 *
 * @example
 * // Allow analysts and above (not readonly) to access tasks
 * <ProtectedRoute allowedRoles={['analyst', 'reviewer', 'publisher']}>
 *   <TasksPage />
 * </ProtectedRoute>
 *
 * @example
 * // Any authenticated user can access (no role restriction)
 * <ProtectedRoute>
 *   <AssetsPage />
 * </ProtectedRoute>
 */
export function ProtectedRoute({
  children,
  allowedRoles,
  unauthorizedRedirect = '/unauthorized',
  loginRedirect = '/login',
}: ProtectedRouteProps) {
  const user = useAuthStore((state) => state.user);
  const isLoading = useAuthStore((state) => state.isLoading);
  const location = useLocation();

  // Show loading spinner while auth state is being initialized
  // This prevents flash of login page for authenticated users
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Redirect to login if user is not authenticated
  // Preserve the current location so user can be redirected back after login
  if (!user) {
    return (
      <Navigate
        to={loginRedirect}
        state={{ from: location }}
        replace
      />
    );
  }

  // Check role-based access if allowedRoles is specified
  // If the user's role is not in the allowed list, redirect to unauthorized page
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return (
      <Navigate
        to={unauthorizedRedirect}
        replace
      />
    );
  }

  // User is authenticated and authorized, render children
  return <>{children}</>;
}

/**
 * Type export for ProtectedRoute props
 * Useful for extending the component or creating custom route guards
 */
export type { ProtectedRouteProps };

/**
 * ROLE_HIERARCHY constant for role-based comparisons
 * Higher index = more permissions
 */
const ROLE_HIERARCHY: Record<Role, number> = {
  readonly: 0,
  analyst: 1,
  reviewer: 2,
  publisher: 3,
};

/**
 * Helper function to check if a role meets a minimum role requirement
 * Follows the hierarchy: readonly < analyst < reviewer < publisher
 *
 * @param userRole - The user's current role
 * @param minimumRole - The minimum required role
 * @returns true if userRole meets or exceeds minimumRole
 *
 * @example
 * hasMinimumRole('reviewer', 'analyst') // true
 * hasMinimumRole('analyst', 'reviewer') // false
 */
export function hasMinimumRole(userRole: Role, minimumRole: Role): boolean {
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[minimumRole];
}

/**
 * Get all roles that meet or exceed a minimum role requirement
 * Useful for generating the allowedRoles array for ProtectedRoute
 *
 * @param minimumRole - The minimum required role
 * @returns Array of roles that meet or exceed the minimum
 *
 * @example
 * getRolesAtOrAbove('analyst') // ['analyst', 'reviewer', 'publisher']
 * getRolesAtOrAbove('publisher') // ['publisher']
 */
export function getRolesAtOrAbove(minimumRole: Role): Role[] {
  const minLevel = ROLE_HIERARCHY[minimumRole];
  return (Object.entries(ROLE_HIERARCHY) as [Role, number][])
    .filter(([, level]) => level >= minLevel)
    .map(([role]) => role);
}

/**
 * Pre-defined role arrays for common route access patterns
 * Use these constants with ProtectedRoute for consistency
 */
export const ALLOWED_ROLES = {
  /** All authenticated users (including readonly) */
  ALL: ['readonly', 'analyst', 'reviewer', 'publisher'] as Role[],
  /** Analyst and above (excludes readonly) */
  ANALYST_AND_ABOVE: ['analyst', 'reviewer', 'publisher'] as Role[],
  /** Reviewer and above (excludes analyst and readonly) */
  REVIEWER_AND_ABOVE: ['reviewer', 'publisher'] as Role[],
  /** Publisher only (admin access) */
  PUBLISHER_ONLY: ['publisher'] as Role[],
};
